import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { itemsSuccess } from "../redux/items";
import {
  snackMessage,
  openSnack,
  Load,
  toggleOpenDeleteDialog,
  toggleOpenItemEditDialog,
  toggleExpiryAlert,
  toggleOpenRestockDialog,
  toggleCollapseItems,
} from "../redux/GlobalStates";
import { request } from "../request";
import moment from "moment";
const useItems = () => {
  const allItems = useSelector((state) => state.items.Items);
  const storeId = localStorage.getItem("storeId");
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const [openAdd, setOpenAdd] = useState(false);
  const [newItem, setNewItem] = useState({
    name: "",
    quantity: "",
    expiry: "",
    createdAt: moment().format("DD/MM/YYYY"),
    price: "",
    storeId: storeId,
    cost_price: "",
    expires: true,
    wholesale_price: "",
    barcode: "",
  });

  const collapseItemsSection = () => dispatch(toggleCollapseItems());
  const ExpiryAlertToggler = () => dispatch(toggleExpiryAlert());
  const toggleAddFormDialog = () => setOpenAdd((prev) => !prev);
  const toggleEditForm = () => dispatch(toggleOpenItemEditDialog());

  const [items, setItems] = useState(
    [...allItems]?.sort(
      (a, b) => a?.name?.toLowerCase() - b?.name?.toLowerCase()
    )
  );
  // expiring items
  const expiringItems = useMemo(() => {
    const today = moment();
    const threeMonthsFromNow = moment().add(3, "months");

    return allItems.filter((item) => {
      const expiry = moment(item?.expiry, "DD/MM/YYYY");
      return (
        item?.expires &&
        expiry?.isAfter(today) &&
        expiry?.isBefore(threeMonthsFromNow)
      );
    });
  }, [allItems]);
  // inventory value
  const costInStock = useMemo(
    () =>
      allItems?.reduce((a, b) => a + Number(b.quantity) * Number(b.price), 0),
    [allItems]
  );
  // expired value
  const expiredItems = allItems?.filter(
    (item) =>
      item?.expires && moment(item?.expiry, "DD/MM/YYYY").isBefore(moment())
  );
  const expCost = useMemo(
    () => expiredItems?.reduce((a, b) => a + Number(b.quantity) * b.price, 0),
    [expiredItems]
  );
  // console.log();

  // add item
  const handleAddNewItem = async () => {
    dispatch(Load());
    if (
      !newItem.name ||
      newItem.quantity < 0 ||
      newItem.price < 0 ||
      newItem.cost_price < 0 ||
      (newItem.expires && !newItem.expiry)
    ) {
      dispatch(
        snackMessage("Provide value for Name, Quantity, Price and/or Expiry")
      );
    } else if (
      allItems.find(
        (item) =>
          (item.barcode && item.barcode === newItem.barcode) ||
          item.name === newItem.name
      )
    ) {
      dispatch(snackMessage("Item already exists!"));
    } else {
      let item = {
        ...newItem,
      };
      try {
        const res = await request.post("/items", item);
        dispatch(
          itemsSuccess([
            {
              ...item,
              _id: res.data._id,
              id: res.data.id,
              restock_history: [
                {
                  stocked_at: item.createdAt,
                  prev_stock: 0,
                  new_stock: item.quantity,
                  current_stock: item.quantity,
                },
              ],
            },
            ...allItems,
          ])
        );
        dispatch(snackMessage(res.data.message));
        setNewItem({
          name: "",
          quantity: "",
          expiry: "",
          createdAt: moment().format("DD/MM/YYYY"),
          price: "",
          storeId: storeId,
          cost_price: "",
          wholesale_price: "",
          expires: item.expires,
          barcode: "",
        });
      } catch (err) {
        dispatch(
          snackMessage(err.response ? err.response.data : "Network error")
        );
        console.log(err);
      }
    }
    dispatch(openSnack());
    dispatch(Load());
  };

  // search reset
  const handleSearchReset = () => {
    setSearch("");
    setItems([...allItems]);
  };
  // handle search
  const handleSearch = (term) => {
    setSearch(term.target.value);
    setItems(
      [...allItems]
        ?.filter(
          (item) =>
            item.name &&
            item.name?.toLowerCase().indexOf(term.target.value.toLowerCase()) >
              -1
        )
        ?.sort((a, b) =>
          a.name?.toLowerCase() > b.name?.toLowerCase() ? 1 : -1
        )
    );
  };

  // delete item
  const handleDeleteItem = async (id) => {
    dispatch(Load());
    try {
      const res = await request.delete(`/items/${id}`, {});
      dispatch(snackMessage(res.data));
      dispatch(itemsSuccess(allItems.filter((item) => item._id !== id)));
      dispatch(snackMessage(res.data));
    } catch (err) {
      dispatch(
        snackMessage(err.response ? err.response.data : "Network error!")
      );
    }
    dispatch(Load());
    dispatch(openSnack());
    dispatch(toggleOpenDeleteDialog());
  };
  // delete multiple items
  const deleteSelectedItems = async (selectedItems, setSelectedItems) => {
    dispatch(Load());

    if (selectedItems.length < 1) {
      dispatch(snackMessage("No item selected!"));
    } else {
      try {
        const res = await request.delete("/items/bulk/delete", {
          data: { selectedItems },
        });
        dispatch(snackMessage(res.data));
        dispatch(
          itemsSuccess(
            allItems.filter((item) => !selectedItems.includes(item.id))
          )
        );
        setSelectedItems([]);
      } catch (err) {
        dispatch(
          snackMessage(err.response ? err.response.data : "Network error!")
        );
        console.log(err);
      }
    }
    dispatch(Load());
    dispatch(openSnack());
  };
  // edit item
  const handleEdit = async (item) => {
    dispatch(Load());
    try {
      const res = await request.put(`/items/${item._id}`, { item });
      const otherItems = allItems?.filter((data) => data._id !== item._id);
      dispatch(itemsSuccess([item, ...otherItems]));
      setItems([item, ...otherItems]);
      dispatch(snackMessage(res.data));
      toggleEditForm();
      handleSearchReset();
    } catch (err) {
      dispatch(
        snackMessage(err.response ? err.response.data : "Network error")
      );
    }
    dispatch(Load());
    dispatch(openSnack());
  };
  // restock item
  const toggleRestockDialog = () => dispatch(toggleOpenRestockDialog());
  const handleRestock = async (item) => {
    dispatch(Load());
    if (!item.new_stock || item.new_stock < 1) {
      dispatch(snackMessage("Invalid stock number!"));
    } else {
      try {
        const res = await request.put(`/items/restock/${item.id}`, {
          item,
        });
        let restocked_item = items?.find((data) => data._id === item.id);
        restocked_item = {
          ...restocked_item,
          quantity: item.current_stock,
          restock_history: [
            { ...item, id: Math.floor(Math.random() * 99999).toString() },
            ...restocked_item?.restock_history,
          ],
        };
        const updatedItems = allItems?.map((i) =>
          i._id === item.id ? restocked_item : i
        );
        toggleRestockDialog();
        dispatch(snackMessage(res.data));
        dispatch(itemsSuccess(updatedItems));
        setSearch("");
      } catch (err) {
        dispatch(
          snackMessage(err.response ? err.response.data : "Network error")
        );
      }
    }
    dispatch(Load());
    dispatch(openSnack());
  };
  return {
    search,
    setItems,
    setSearch,
    items,
    allItems,
    handleSearch,
    handleSearchReset,
    handleDeleteItem,
    toggleEditForm,
    handleEdit,
    openAdd,
    toggleAddFormDialog,
    newItem,
    setNewItem,
    handleAddNewItem,
    ExpiryAlertToggler,
    handleRestock,
    toggleRestockDialog,
    deleteSelectedItems,
    collapseItemsSection,
    costInStock,
    expiredItems,
    expCost,
    expiringItems,
  };
};

export default useItems;
